import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import TensionReelController from "./TensionReelController";
import { safeInit } from "../../../utilities";

const TensionReel = forwardRef(
  ({ index, reelSpacing, symbolWidth, symbolHeight }, ref) => {
    const tensionReelControllerRef = useRef(null);
    const tensionContainerRef = useRef(null);

    // Memoize container X position
    const containerX = useMemo(
      () => reelSpacing * (index - 2),
      [reelSpacing, index]
    );

    // Initialize TensionReelController when assets are ready
    useEffect(() => {
      if (tensionContainerRef.current && !tensionReelControllerRef.current) {
        safeInit(() => {
          tensionReelControllerRef.current = new TensionReelController(
            tensionContainerRef.current,
            index,
            symbolWidth,
            symbolHeight
          );
        });
      }

      return () => {
        if (tensionReelControllerRef.current) {
          tensionReelControllerRef.current.cleanup?.();
          tensionReelControllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle symbol size changes for responsive design
    useEffect(() => {
      if (tensionReelControllerRef.current) {
        tensionReelControllerRef.current.resizeTension(
          symbolWidth,
          symbolHeight
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbolHeight]);

    // Expose controller methods to parent components
    useImperativeHandle(ref, () => ({
      playTensionIn: () => {
        tensionReelControllerRef.current?.playTensionIn();
      },

      playTensionOut: () => {
        tensionReelControllerRef.current?.playTensionOut();
      },

      hideTension: () => {
        tensionReelControllerRef.current?.hideTension();
      },

      forceStopTension: () => {
        tensionReelControllerRef.current?.forceStopTension();
      },

      getTensionOutDuration: () => {
        return tensionReelControllerRef.current?.getTensionOutDuration() || 1000;
      },

      getTensionCycleDuration: () => {
        return tensionReelControllerRef.current?.getTensionCycleDuration() || null;
      },

      isTensionVisible: () => {
        return tensionReelControllerRef.current?.isTensionVisible() || false;
      },

      pauseTension: () => {
        tensionReelControllerRef.current?.pauseTension();
      },

      resumeTension: () => {
        tensionReelControllerRef.current?.resumeTension();
      },

      get _isTensionPaused() {
        return tensionReelControllerRef.current?._isTensionPaused || false;
      },

      clearAllTimeouts: () => {
        tensionReelControllerRef.current?.clearAllTimeouts();
      },
    }));

    return (
      <Container
        ref={tensionContainerRef}
        name={`tension__spine_${index}`}
        sortableChildren
        x={containerX}
      ></Container>
    );
  }
);

export default TensionReel;
